import axios from 'axios';
import { IUser } from '@sravni/types/lib/auth';
import { getSilentSignInUrl, getSilentSignInVerifyUrl } from '@sravni/utils/lib/auth';
import getConfig from 'next/config';
import {
    ImportUserParams,
    ImportUserResponse,
    SignInParams,
    SignInResponse,
    VerifyParams,
    VerifyResponse,
} from '../../services/identity/identity.types';
import { ISuggestLocationsParams } from '../../services/location/location.types';
import { Model, Brand, ModelPowersProps, CarInfo } from '../../services/auto/auto.types';
import {
    ScoreRequestParams,
    ScoreClientResponse,
    ScoreClientRequestParams,
} from '../../services/amazingScoring/amazingScoring.types';
import { CallCenter } from '../../server/api/auth/types/callcenter';
import { CaptchaClient } from '../captcha';
import { isAxiosError } from '../typeguards';
import { retry } from '../index';
import { HEADERS } from '../../constants';

export class ClientProvider {
    constructor(protected path: string) {}

    locationSuggest = async (params: ISuggestLocationsParams): Promise<Array<Location.SuggestItem>> => {
        const { data } = await axios.get<Array<Location.SuggestItem>>(`${this.path}/suggest/location/`, {
            params,
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        return data;
    };

    locationForRoute = async (address: string): Promise<Location.Item | null> => {
        const { data } = await axios.get<Location.Item | null>(`${this.path}/location/`, {
            params: { address },
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        return data;
    };

    locationSearchById = async (id: number): Promise<[Location.SuggestItem]> => {
        const { data } = await axios.get<[Location.SuggestItem]>(`${this.path}/location/search/`, {
            params: { ids: id },
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        return data;
    };

    locationSearchByRoute = async (route: string): Promise<[Location.SuggestItem]> => {
        const { data } = await axios.get<[Location.SuggestItem]>(`${this.path}/location/search/`, {
            params: { route },
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        return data;
    };

    signIn = async (params: SignInParams): Promise<SignInResponse> => {
        const { webPath, serviceName } = getConfig().publicRuntimeConfig;
        const silentSignInUrl = getSilentSignInUrl({ webPath });
        try {
            await axios.post(silentSignInUrl, params, {
                headers: {
                    [HEADERS.CALLER_ID]: serviceName,
                    [HEADERS.CONTENT_TYPE]: 'application/json',
                },
                timeout: 10_000,
            });
            return { success: true };
        } catch (e) {
            if (isAxiosError(e) && e.response) {
                const {
                    response: { status },
                } = e;

                switch (status) {
                    case 429:
                        return {
                            errorText: `Превышено количество попыток. Повторите позднее`,
                        };
                }
            }

            return {
                errorText: 'Ошибка при запросе кода',
            };
        }
    };

    callCenterSignIn = async (params: CallCenter.SignInParams): Promise<boolean> => {
        const { data } = await axios.post<{ success: boolean }>(`${this.path}/auth/callcenter/`, params);
        return data.success;
    };

    verify = async (params: VerifyParams): Promise<VerifyResponse> => {
        const { webPath, serviceName } = getConfig().publicRuntimeConfig;
        const silentSignInVerifyUrl = getSilentSignInVerifyUrl({ webPath });
        try {
            const { data } = await retry(
                () =>
                    axios.post<VerifyResponse>(silentSignInVerifyUrl, params, {
                        headers: {
                            [HEADERS.CALLER_ID]: serviceName,
                            [HEADERS.CONTENT_TYPE]: 'application/json',
                        },
                    }),
                { count: 2 },
            );
            const { otac, username } = data;
            return {
                otac,
                username,
                errorText: null,
            };
        } catch (e) {
            if (isAxiosError(e) && e.response) {
                const {
                    response: { status },
                } = e;
                switch (status) {
                    case 400:
                        return {
                            errorText: 'Некорректный СМС код',
                        };
                    case 429: {
                        return {
                            errorText: `Превышено количество попыток. Повторите позднее`,
                        };
                    }
                    case 500:
                    case 404:
                    default:
                    /** не делаем ничего, возвращаем ошибку по умолчанию */
                }
            }

            return {
                errorText: 'Не удалось авторизоваться',
            };
        }
    };

    signInSilent = async (params: ImportUserParams): Promise<ImportUserResponse> => {
        const { data } = await axios.post<ImportUserResponse>(`${this.path}/auth/silent/`, params);
        return data;
    };

    account = async (): Promise<IUser | null> => {
        const { data } = await axios.get<IUser | null>(`${this.path}/auth/account/`, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            withCredentials: true,
        });
        return data;
    };

    carBrands = async () => {
        const { data } = await axios.post<Array<Brand>>(`${this.path}/suggest/car-brands/`);
        return data;
    };

    carModels = async (params: { brandId: number | string }) => {
        const { data } = await axios.post<Array<Model>>(`${this.path}/suggest/car-models/`, params);
        return data;
    };

    carModelYears = async (params: { modelId: number | string }) => {
        const { data } = await axios.post<Array<number>>(`${this.path}/suggest/car-model-years/`, params);
        return data;
    };

    carModelPowers = async (params: ModelPowersProps) => {
        const { data } = await axios.post<Array<number>>(`${this.path}/suggest/car-model-powers/`, params);
        return data;
    };

    /** with captcha v3 */
    carInfoByNumber = async (params: { carNumber: string }) => {
        const captcha = await CaptchaClient.execute('carInfoByNumber');
        const { data } = await axios.post<CarInfo | null>(`${this.path}/suggest/car-info-by-number/`, {
            ...params,
            captcha,
        });
        return data;
    };

    postScoringData = async (userData: ScoreClientRequestParams): Promise<ScoreClientResponse> => {
        const { data } = await axios.post<ScoreClientResponse>(`${this.path}/scoring/data/`, userData);
        return data;
    };
}
